import { useDialog } from '@globals/dialog/useDialogStore'
import { useApiActionToast } from '@globals/toaster/useToasterStore'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  AwardType,
  NominateParams,
  TenantMemberDisplayData,
  listCandidates,
  nominate,
} from '@kudos/http-client'
import { useApiError } from '@shared/errors/useApiError'
import Cancel from '@shared/forms/Cancel'
import FormContextWrapper from '@shared/forms/FormContextWrapper'
import Submit from '@shared/forms/Submit'
import TextAreaField from '@shared/forms/TextAreaField'
import {
  SearchResults,
  TenantMemberSearchFn,
} from '@shared/forms/tenantMemberAndTeamSearchField/useSearchTenantMembersAndTeams'
import TenantMemberSearchField from '@shared/forms/tenantMemberSearchField/TenantMemberSearchField'
import { useInvalidateQuery } from '@shared/hooks/useInvalidateQuery'
import { MAX_MESSAGE_CHARS } from '@shared/wallPost/constants'
import { useMutation } from '@tanstack/react-query'
import { PINNED_TIMELINE_ITEMS_QK } from '@tenant/home/user/upcomingAndImportantSection/usePinnedTimelineItems'
import { t } from '@transifex/native'
import { T, useT } from '@transifex/react'
import { ReactNode, useEffect } from 'react'
import { FieldValues, UseFormSetError, useForm } from 'react-hook-form'
import { z } from 'zod'
import useMyAwards from '../useMyAwards'
import { SEND_NOMINATIONS_MODAL_KEY } from './SendNominationsModal'
import { useSendNominationFormStore } from './useSendNominationFormStore'

type NominateFormProps = {
  nomination: AwardType
  isPinned?: boolean
}

const NominateFormSchema = z.object({
  nominee: z
    .array(z.custom<TenantMemberDisplayData>())
    .nonempty({ message: t('Nominee is required') }),
  message: z
    .string()
    .trim()
    .min(1, { message: t('Reason is required') })
    .max(MAX_MESSAGE_CHARS, {
      message: t(`Message should not be more than ${MAX_MESSAGE_CHARS} characters`),
    }),
})

type NominateFormData = z.infer<typeof NominateFormSchema>

const defaultValues: NominateFormData = {
  // @ts-expect-error nominee should be empty by default
  nominee: [],
  message: '',
}

const NominateForm: React.FC<NominateFormProps> = ({ nomination, isPinned }) => {
  const invalidateQuery = useInvalidateQuery()
  const { refetch } = useMyAwards(false)
  const [showSuccess] = useApiActionToast()
  const { handleApiError } = useApiError()
  const t = useT()
  const { closeDialog: closeNominationDialog } = useDialog(SEND_NOMINATIONS_MODAL_KEY)
  const { setIsFormDirty } = useSendNominationFormStore()

  const methods = useForm<NominateFormData>({
    resolver: zodResolver(NominateFormSchema),
    defaultValues,
  })

  useEffect(() => {
    setIsFormDirty(methods.formState.isDirty)
  }, [methods.formState.isDirty])

  const { mutate: createNomination } = useMutation({
    mutationFn: (data: NominateParams) => nominate(nomination.id, data),
    onSuccess: async () => {
      if (isPinned) {
        await invalidateQuery([PINNED_TIMELINE_ITEMS_QK])
      } else {
        refetch()
      }
      closeNominationDialog()
      showSuccess(t('Nomination sent successfully'))
    },
    onError: error =>
      handleApiError(error, methods.setError as UseFormSetError<FieldValues>, [
        'nominee',
        'message',
      ]),
  })

  const onSubmit = (formData: NominateFormData) => {
    createNomination({
      nomineeId: formData.nominee[0].id,
      message: formData.message,
    })
  }

  const searchFn: TenantMemberSearchFn = async ({ search }) => {
    const data = await listCandidates(nomination.id, {
      search,
    })
    return data as unknown as SearchResults
  }

  const FormActions: ReactNode = (
    <>
      <Cancel elementId="btnNominateFormCancel" onClick={closeNominationDialog} />
      <Submit
        elementId="btnNominate"
        formState={methods.formState}
        submitText={<T _str="Submit" />}
      />
    </>
  )

  return (
    <FormContextWrapper formMethods={methods} onSubmit={onSubmit} formActions={FormActions}>
      <TenantMemberSearchField
        name="nominee"
        control={methods.control}
        label={<T _str="Choose someone to nominate" />}
        help={t('Ineligible users and users you’ve already nominated will not show')}
        searchFn={searchFn}
        multiple={false}
        zIndexPlacement="modal"
      />
      <TextAreaField
        name="message"
        control={methods.control}
        label={<T _str="Reason for nomination" />}
        placeholder={t(
          'Describe why you’re nominating this individual and highlight their contributions or impact',
        )}
        maxChars={MAX_MESSAGE_CHARS}
        hideCounter
      />
    </FormContextWrapper>
  )
}

export default NominateForm
